import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import type { CreateVehicleMaintenanceRequest } from "@/types";
import { createCrudHooks } from "@/hooks/crudHooksFactory";

// Query keys
const vehicleMaintenanceKeys = {
  all: ["vehicleMaintenance"] as const,
  lists: () => [...vehicleMaintenanceKeys.all, "list"] as const,
  list: (filters: string) =>
    [...vehicleMaintenanceKeys.lists(), { filters }] as const,
  details: () => [...vehicleMaintenanceKeys.all, "detail"] as const,
  detail: (id: string) => [...vehicleMaintenanceKeys.details(), id] as const,
  byVehicle: (vehicleId: string) =>
    [...vehicleMaintenanceKeys.all, "byVehicle", vehicleId] as const,
  recentByVehicle: (vehicleId: string, limit?: number) =>
    [...vehicleMaintenanceKeys.all, "recent", vehicleId, limit] as const,
};

// Get maintenance records with filters
export function useVehicleMaintenance(params?: {
  vehicleId?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  offset?: number;
  orderBy?: "performedAt" | "createdAt" | "mileage";
  orderDirection?: "asc" | "desc";
}) {
  const queryKey = vehicleMaintenanceKeys.list(JSON.stringify(params || {}));

  return useQuery({
    queryKey,
    queryFn: () => apiClient.getVehicleMaintenance(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Get single maintenance record
export function useMaintenanceRecord(id: string) {
  return useQuery({
    queryKey: vehicleMaintenanceKeys.detail(id),
    queryFn: () => apiClient.getMaintenanceRecord(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get maintenance records by vehicle ID
export function useVehicleMaintenanceByVehicle(vehicleId: string) {
  return useQuery({
    queryKey: vehicleMaintenanceKeys.byVehicle(vehicleId),
    queryFn: () => apiClient.getVehicleMaintenanceByVehicle(vehicleId),
    enabled: !!vehicleId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Get recent maintenance records by vehicle ID
export function useRecentMaintenanceByVehicle(
  vehicleId: string,
  limit?: number,
) {
  return useQuery({
    queryKey: vehicleMaintenanceKeys.recentByVehicle(vehicleId, limit),
    queryFn: () => apiClient.getRecentMaintenanceByVehicle(vehicleId, limit),
    enabled: !!vehicleId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

const maintenanceCrudHooks = createCrudHooks({
  create: {
    mutationFn: (data: CreateVehicleMaintenanceRequest) =>
      apiClient.createMaintenanceRecord(data),
    cacheActions: [
      // Invalidate and refetch lists
      { type: "invalidate", key: vehicleMaintenanceKeys.lists() },
      {
        type: "invalidate",
        key: (newRecord) =>
          vehicleMaintenanceKeys.byVehicle(newRecord.vehicleId),
      },
      {
        type: "invalidate",
        key: (newRecord) =>
          vehicleMaintenanceKeys.recentByVehicle(newRecord.vehicleId),
      },
    ],
  },
  update: {
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateVehicleMaintenanceRequest>;
    }) => apiClient.updateMaintenanceRecord(id, data),
    cacheActions: [
      // Update the specific record in cache
      {
        type: "setQueryData",
        key: (updatedRecord) => vehicleMaintenanceKeys.detail(updatedRecord.id),
      },
      // Invalidate lists to ensure consistency
      { type: "invalidate", key: vehicleMaintenanceKeys.lists() },
      {
        type: "invalidate",
        key: (updatedRecord) =>
          vehicleMaintenanceKeys.byVehicle(updatedRecord.vehicleId),
      },
      {
        type: "invalidate",
        key: (updatedRecord) =>
          vehicleMaintenanceKeys.recentByVehicle(updatedRecord.vehicleId),
      },
    ],
  },
  delete: {
    mutationFn: ({ id }: { id: string; vehicleId: string }) =>
      apiClient.deleteMaintenanceRecord(id),
    cacheActions: [
      // Remove from cache
      {
        type: "removeQueries",
        key: (_, { id }) => vehicleMaintenanceKeys.detail(id),
      },
      // Invalidate lists
      { type: "invalidate", key: vehicleMaintenanceKeys.lists() },
      {
        type: "invalidate",
        key: (_, { vehicleId }) => vehicleMaintenanceKeys.byVehicle(vehicleId),
      },
      {
        type: "invalidate",
        key: (_, { vehicleId }) =>
          vehicleMaintenanceKeys.recentByVehicle(vehicleId),
      },
    ],
  },
});

export const useCreateMaintenanceRecord = maintenanceCrudHooks.useCreate;
export const useUpdateMaintenanceRecord = maintenanceCrudHooks.useUpdate;
export const useDeleteMaintenanceRecord = maintenanceCrudHooks.useDelete;

// Search maintenance records
export function useSearchMaintenanceRecords(query: string, limit?: number) {
  return useQuery({
    queryKey: ["vehicleMaintenance", "search", query, limit],
    queryFn: () => apiClient.searchMaintenanceRecords(query, limit),
    enabled: !!query && query.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  });
}
